import React, { Children, useCallback } from "react";
import { Paper, Typography, Button, Box, useTheme } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { FileSpreadsheet, FileText } from "lucide-react";

interface DropZoneProps {
  onImportClick: (files?: File[]) => void;
  header: string;
  subHeader: string;
  children?: React.ReactNode;
  buttonText: string;
  icon: "spreadsheet" | "document";
  maxFileSize?: number;
  allowDrop?: boolean;
}

const DropZone: React.FC<DropZoneProps> = ({
  onImportClick,
  header,
  subHeader,
  children,
  buttonText,
  icon,
  maxFileSize = 6 * 1024 * 1024, // Default 6 MB
  allowDrop = true,
}) => {
  const theme = useTheme();

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (acceptedFiles.length > 0) {
        onImportClick(acceptedFiles);
      }
    },
    [onImportClick]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    accept:
      icon === "spreadsheet"
        ? {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
              [".xlsx"],
            "application/vnd.ms-excel": [".xls"],
            "text/csv": [".csv"],
          }
        : {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".jpg", ".jpeg"],
          },
    multiple: icon === "document", // Allow multiple files for document uploads
    noDrag: !allowDrop,
  });

  const IconComponent = icon === "spreadsheet" ? FileSpreadsheet : FileText;

  return (
    <Paper
      {...getRootProps()}
      sx={{
        flex: 1,
        p: 3,
        textAlign: "center",
        cursor: allowDrop ? "pointer" : "default",
        border: "2px dashed",
        borderColor: isDragActive ? "primary.main" : "divider",
        backgroundColor: isDragActive ? "rgba(0, 0, 0, 0.02)" : "transparent",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "primary.light",
          backgroundColor: "rgba(0, 0, 0, 0.01)",
        },
      }}
      elevation={0}
    >
      {allowDrop && <input {...getInputProps()} />}
      <Box sx={{ mb: 2 }}>
        <IconComponent
          size={40}
          color={theme.palette.primary.main}
          style={{ marginBottom: 8 }}
        />
      </Box>
      <Typography variant="subtitle1" gutterBottom>
        {header}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subHeader}
      </Typography>
      {children}

      <Button
        variant="outlined"
        color="primary"
        size="medium"
        onClick={(e) => {
          e.stopPropagation();
          onImportClick();
        }}
      >
        {buttonText}
      </Button>
    </Paper>
  );
};

export default DropZone;
